// src/plugins/sanitize/index.js
import DOMPurify from "dompurify";

/** =========================
 *  Provider Registry
 *  =========================
 *  每個 provider 定義：
 *  - name: 唯一識別
 *  - match(url: URL): 是否放行（通常檢查 hostname/path）
 *  - tags: 需要放行的標籤（預設含 'iframe'）
 *  - attrs: 需要放行的屬性（會與全域的基本屬性合併）
 *  - enforce(el: HTMLElement): 清洗後的 DOM 後處理（e.g. 加 sandbox）
 */
const providerRegistry = new Map();

export function registerProvider(name, def) {
  if (!name || typeof def?.match !== "function") {
    throw new Error("registerProvider(name, { match, tags?, attrs?, enforce? }) is required");
  }
  providerRegistry.set(name, {
    name,
    tags: ["iframe", ...(def.tags || [])],
    attrs: ["src", "width", "height", "title", "allow", "allowfullscreen", "frameborder", "referrerpolicy", "sandbox", ...(def.attrs || [])],
    match: def.match,
    enforce: def.enforce || (() => {}),
  });
}

export function hasProvider(name) {
  return providerRegistry.has(name);
}

export function listProviders() {
  return [...providerRegistry.keys()];
}

/** =========================
 *  Built-in Providers
 *  ========================= */
registerProvider("youtube", {
  match(url) {
    const host = url.hostname.replace(/^www\./, "");
    const okHost = host === "youtube.com" || host === "youtube-nocookie.com";
    return okHost && url.pathname.startsWith("/embed/");
  },
  enforce(root) {
    const ifr = root.querySelector("iframe");
    if (ifr && !ifr.hasAttribute("sandbox")) {
      ifr.setAttribute("sandbox", "allow-same-origin allow-scripts allow-presentation allow-popups");
    }
  },
});

registerProvider("google-drive", {
  match(url) {
    const host = url.hostname.replace(/^www\./, "");
    // 典型嵌入：
    // https://drive.google.com/file/d/xxx/preview
    return host === "drive.google.com" && url.pathname.startsWith("/file/") && url.pathname.includes("/preview");
  },
  attrs: [
    // 如果你有額外想保留的屬性可以寫在這
    "allowfullscreen",
  ],
  enforce(root) {
    const ifr = root.querySelector("iframe");
    if (ifr && !ifr.hasAttribute("sandbox")) {
      // 視你的需求調整 sandbox 權限
      ifr.setAttribute("sandbox", "allow-same-origin allow-scripts allow-forms allow-popups");
    }
  },
});

registerProvider("vimeo", {
  match(url) {
    const host = url.hostname.replace(/^www\./, "");
    return (host === "vimeo.com" || host === "player.vimeo.com") && url.pathname.startsWith("/video/");
  },
  enforce(root) {
    const ifr = root.querySelector("iframe");
    if (ifr && !ifr.hasAttribute("sandbox")) {
      ifr.setAttribute("sandbox", "allow-same-origin allow-scripts allow-presentation");
    }
  },
});

registerProvider("spotify", {
  match(url) {
    const host = url.hostname.replace(/^www\./, "");
    return host === "open.spotify.com" && url.pathname.startsWith("/embed");
  },
  enforce(root) {
    const ifr = root.querySelector("iframe");
    if (ifr && !ifr.hasAttribute("sandbox")) {
      ifr.setAttribute("sandbox", "allow-same-origin allow-scripts");
    }
  },
});

registerProvider("google-maps", {
  match(url) {
    const host = url.hostname.replace(/^www\./, "");
    // 典型嵌入：https://www.google.com/maps/embed?...
    return (host === "google.com" || host === "maps.google.com" || host === "www.google.com") && url.pathname.startsWith("/maps") && (url.pathname.includes("/embed") || url.searchParams.has("pb"));
  },
  enforce(root) {
    const ifr = root.querySelector("iframe");
    if (ifr && !ifr.hasAttribute("sandbox")) {
      ifr.setAttribute("sandbox", "allow-same-origin allow-scripts allow-pointer-lock"); // 視需求增減
    }
  },
});

/** =========================
 *  Optional: 全域 IMG 輕清理（可依你原本需求）
 *  ========================= */
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "IMG") {
    node.removeAttribute("width");
    node.removeAttribute("height");
    if (node.hasAttribute("style")) {
      const s = node
        .getAttribute("style")
        .replace(/width\s*:\s*[^;]+;?/gi, "")
        .replace(/height\s*:\s*[^;]+;?/gi, "");
      if (s.trim()) node.setAttribute("style", s);
      else node.removeAttribute("style");
    }
    node.setAttribute("loading", "lazy");
  }
});

/** =========================
 *  Core Sanitize Helpers
 *  ========================= */

/** 一般安全清洗（不允許 iframe） */
export function sanitizeHtml(html) {
  return DOMPurify.sanitize(html || "");
}

/** 允許指定 providers 的 iframe 白名單清洗 */
export function sanitizeEmbed(html, providers = []) {
  const allowSet = new Set(providers.map((p) => (typeof p === "string" ? p : p?.name)).filter(Boolean));

  const allowed = [...allowSet]
    .map((name) => {
      const p = providerRegistry.get(name);
      if (!p) console.warn(`[sanitize] Unknown provider: ${name}`);
      return p;
    })
    .filter(Boolean);

  // 如果沒指定已知 provider，就直接當作一般清洗
  if (!allowed.length) return sanitizeHtml(html);

  // 1) 動態掛載 src 白名單 hook
  const srcHook = (node, data) => {
    if (node.nodeName !== "IFRAME" || data.attrName !== "src") return;
    try {
      const url = new URL(data.attrValue, window.location.origin);
      const ok = allowed.some((p) => p.match(url));
      if (!ok) data.keepAttr = false;
    } catch {
      data.keepAttr = false;
    }
  };
  DOMPurify.addHook("uponSanitizeAttribute", srcHook);

  // 2) 聚合允許的 tag / attr
  const ADD_TAGS = Array.from(new Set(allowed.flatMap((p) => p.tags)));
  const ADD_ATTR = Array.from(new Set(allowed.flatMap((p) => p.attrs)));

  // 3) 執行清洗
  const out = DOMPurify.sanitize(html || "", { ADD_TAGS, ADD_ATTR });

  // 4) 移除 hook，避免汙染其他流程
  DOMPurify.removeHook("uponSanitizeAttribute", srcHook);

  // 5) 清洗後 DOM 後處理（加 sandbox 等）
  const wrap = document.createElement("div");
  wrap.innerHTML = out;
  allowed.forEach((p) => p.enforce(wrap));

  return wrap.innerHTML;
}

/** =========================
 *  Vue2 Plugin
 *  =========================
 *  - v-safe-html：一般清洗
 *  - v-embed：允許白名單 iframe
 *      用法 A：修飾符指定 provider，如 v-embed.youtube.vimeo
 *      用法 B：binding 傳入 { providers: ['youtube','vimeo'] }
 */
export default {
  install(Vue) {
    Vue.directive("safe-html", {
      bind(el, binding) {
        el.innerHTML = sanitizeHtml(binding.value);
      },
      update(el, binding) {
        if (binding.value !== binding.oldValue) {
          el.innerHTML = sanitizeHtml(binding.value);
        }
      },
    });

    Vue.directive("embed", {
      bind(el, binding) {
        const providers = collectProviders(binding);
        el.innerHTML = sanitizeEmbed(binding.value, providers);
      },
      update(el, binding) {
        if (binding.value !== binding.oldValue || JSON.stringify(collectProviders(binding)) !== JSON.stringify(collectProviders({ ...binding, oldValue: {} }))) {
          const providers = collectProviders(binding);
          el.innerHTML = sanitizeEmbed(binding.value, providers);
        }
      },
    });
  },
};

// 解析修飾符/參數的 providers
function collectProviders(binding) {
  // A) 修飾符：v-embed.youtube.vimeo
  const mods = Object.keys(binding.modifiers || {}).filter((m) => hasProvider(m));
  // B) 參數：v-embed:youtube 或 v-embed:[ 'youtube','vimeo' ]
  const arg = Array.isArray(binding.arg) ? binding.arg : binding.arg ? [binding.arg] : [];
  const fromArg = arg.filter((n) => hasProvider(n));
  // C) 值：{ providers: ['youtube','vimeo'] }
  const fromVal = Array.isArray(binding?.value?.providers) ? binding.value.providers.filter(hasProvider) : [];

  const set = new Set([...mods, ...fromArg, ...fromVal]);
  return [...set];
}

// 使用範例:
// <div v-embed.youtube.vimeo="htmlWithEmbeds"></div>
// <!-- 參數： -->
// <div v-embed:youtube="html"></div>
// <!-- 值： -->
// <div v-embed="{ providers: ['youtube', 'google-maps'] , html }"></div>

// 之後要新增:
// registerProvider('tiktok', {
//     match(url) {
//       const host = url.hostname.replace(/^www\./, '')
//       return (host === 'tiktok.com' || host === 'www.tiktok.com') &&
//              (url.pathname.startsWith('/embed') || url.pathname.startsWith('/player'))
//     },
//     attrs: ['allowfullscreen'], // 如有額外屬性
//     enforce(root) {
//       const ifr = root.querySelector('iframe')
//       if (ifr && !ifr.hasAttribute('sandbox')) {
//         ifr.setAttribute('sandbox', 'allow-same-origin allow-scripts')
//       }
//     }
//   })
